import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { JourneyPreferences } from '../api-types';
import { formatDay } from '../format';
import { JourneyActions } from './JourneyActions';
import { useJourney } from './use-journey';

/** One journey: its name, where the person is in it and when the next email goes. */
function JourneyState({ journey }: { readonly journey: JourneyPreferences }) {
  const { t, i18n } = useTranslation('communications');
  const name = t(`journey.name.${journey.journey}`, { defaultValue: journey.journey });
  const where = t(`journey.state.${journey.state}`, {
    step: journey.stepNumber,
    steps: journey.stepCount,
    defaultValue: journey.state,
  });
  return (
    <div className="flex flex-col gap-1">
      <p className="text-muted-foreground text-sm">{name}</p>
      <p className="text-base font-semibold">{where}</p>
      {journey.state === 'active' ? (
        <p className="text-muted-foreground text-sm">
          {journey.nextEmailOn === null
            ? t('journey.noNext')
            : t('journey.next', {
                when: formatDay(`${journey.nextEmailOn}T12:00:00`, i18n.resolvedLanguage),
              })}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The profile's «Comunicazioni» (spec §5.1): the journey's state, «Interrompi il percorso»,
 * «Scegli un altro percorso» and «Riprendi il percorso». Every action calls the API, then the new
 * state, as the answer carries it, is shown.
 */
export function CommunicationsSection() {
  const { t } = useTranslation('communications');
  const { state, busy, failed, act, reload } = useJourney();
  return (
    <Card>
      <CardHeader className="px-0">
        <CardTitle className="card-label">{t('journey.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 px-0">
        {state.status === 'loading' ? (
          <p aria-busy="true" className="text-muted-foreground text-sm">
            {t('journey.loading')}
          </p>
        ) : null}
        {state.status === 'error' ? (
          <div className="flex flex-wrap items-center gap-3">
            <p role="alert" className="text-sm">
              {t('common.loadError')}
            </p>
            <Button variant="outline" size="sm" onClick={reload}>
              {t('common.retry')}
            </Button>
          </div>
        ) : null}
        {state.status === 'ready' ? (
          <>
            {state.journeys.length === 0 ? (
              <p className="text-muted-foreground text-sm">{t('journey.none')}</p>
            ) : null}
            {state.journeys.map((journey) => (
              <JourneyState key={journey.journey} journey={journey} />
            ))}
            <JourneyActions journeys={state.journeys} busy={busy} onAct={act} />
            {failed ? (
              <p role="alert" className="text-destructive text-sm">
                {t('journey.failed')}
              </p>
            ) : null}
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
